/* ============================================
   LIGHTBOX COMPONENT
   The Creative Orchestra - GEMINI Framework
   Full-screen image viewer with navigation
   ============================================ */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Download,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
} from 'lucide-react';

/**
 * Lightbox Component
 * Full-screen image viewer with navigation and zoom
 * 
 * @param {Object} props
 * @param {Array} props.images - Array of image objects {url, alt, caption}
 * @param {boolean} props.isOpen - Whether lightbox is open
 * @param {number} props.currentIndex - Current image index
 * @param {Function} props.onClose - Close callback
 * @param {Function} props.onNavigate - Navigation callback (index)
 */
const Lightbox = ({
  images,
  isOpen,
  currentIndex = 0,
  onClose,
  onNavigate,
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const currentImage = images?.[currentIndex];
  const hasMultiple = images?.length > 1;

  // Reset transforms when image changes
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose?.();
          break;
        case 'ArrowLeft':
          if (hasMultiple && currentIndex > 0) {
            onNavigate?.(currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          if (hasMultiple && currentIndex < images.length - 1) {
            onNavigate?.(currentIndex + 1);
          }
          break;
        case '+':
        case '=':
          setZoom(z => Math.min(z + 0.25, 3));
          break;
        case '-':
          setZoom(z => Math.max(z - 0.25, 0.5));
          break;
        case 'r':
          setRotation(r => r + 90);
          break;
        case '0':
          setZoom(1);
          setRotation(0);
          setPosition({ x: 0, y: 0 });
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, hasMultiple, images?.length, onClose, onNavigate]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle zoom with mouse wheel
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(z => Math.max(0.5, Math.min(3, z + delta)));
  }, []);

  // Navigate to previous image
  const goToPrevious = () => {
    if (currentIndex > 0) {
      onNavigate?.(currentIndex - 1);
    }
  };

  // Navigate to next image
  const goToNext = () => {
    if (currentIndex < images.length - 1) {
      onNavigate?.(currentIndex + 1);
    }
  };

  // Reset view
  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  // Download current image
  const downloadImage = () => {
    if (!currentImage?.url) return;
    const link = document.createElement('a');
    link.href = currentImage.url;
    link.download = currentImage.alt || 'image';
    link.click();
  };

  return (
    <AnimatePresence>
      {isOpen && currentImage && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Controls - Top */}
          <div className="absolute top-0 left-0 right-0 z-10 p-4
            flex items-center justify-between
            bg-gradient-to-b from-black/50 to-transparent">
            {/* Image counter */}
            {hasMultiple && (
              <div className="text-white/80 text-sm">
                {currentIndex + 1} / {images.length}
              </div>
            )}

            {/* Toolbar */}
            <div className="flex items-center gap-2 ml-auto">
              <ToolbarButton
                onClick={() => setZoom(z => Math.min(z + 0.25, 3))}
                title="Zoom in (+)"
              >
                <ZoomIn className="w-5 h-5" />
              </ToolbarButton>
              
              <ToolbarButton
                onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}
                title="Zoom out (-)"
              >
                <ZoomOut className="w-5 h-5" />
              </ToolbarButton>
              
              <ToolbarButton
                onClick={() => setRotation(r => r + 90)}
                title="Rotate (R)"
              >
                <RotateCw className="w-5 h-5" />
              </ToolbarButton>
              
              <ToolbarButton
                onClick={resetView}
                title="Reset view (0)"
              >
                <Maximize2 className="w-5 h-5" />
              </ToolbarButton>

              <div className="w-px h-6 bg-white/20 mx-2" />
              
              <ToolbarButton
                onClick={downloadImage}
                title="Download"
              >
                <Download className="w-5 h-5" />
              </ToolbarButton>
              
              <ToolbarButton
                onClick={onClose}
                title="Close (Esc)"
                className="hover:bg-red-500/20"
              >
                <X className="w-5 h-5" />
              </ToolbarButton>
            </div>
          </div>

          {/* Navigation - Previous */}
          {hasMultiple && currentIndex > 0 && (
            <motion.button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10
                p-3 rounded-full bg-black/50 text-white/80
                hover:bg-black/70 hover:text-white
                transition-colors"
              onClick={goToPrevious}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>
          )}

          {/* Navigation - Next */}
          {hasMultiple && currentIndex < images.length - 1 && (
            <motion.button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10
                p-3 rounded-full bg-black/50 text-white/80
                hover:bg-black/70 hover:text-white
                transition-colors"
              onClick={goToNext}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>
          )}

          {/* Image container */}
          <motion.div
            className="relative max-w-[90vw] max-h-[85vh] cursor-grab active:cursor-grabbing"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg) translate(${position.x}px, ${position.y}px)`,
            }}
            drag={zoom > 1}
            dragConstraints={{ left: -200, right: 200, top: -200, bottom: 200 }}
            dragElastic={0.1}
            onWheel={handleWheel}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <img
              src={currentImage.url}
              alt={currentImage.alt}
              className="max-w-full max-h-[85vh] object-contain select-none"
              draggable={false}
            />
          </motion.div>

          {/* Caption */}
          {currentImage.caption && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 z-10 p-6 text-center
                bg-gradient-to-t from-black/80 to-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <p className="text-white text-lg max-w-2xl mx-auto">
                {currentImage.caption}
              </p>
            </motion.div>
          )}

          {/* Thumbnail strip */}
          {hasMultiple && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10
              flex gap-2 p-2 rounded-lg bg-black/50 backdrop-blur-sm
              max-w-[90vw] overflow-x-auto">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => onNavigate?.(index)}
                  className={`
                    flex-shrink-0 w-12 h-12 rounded overflow-hidden
                    border-2 transition-all
                    ${currentIndex === index 
                      ? 'border-white' 
                      : 'border-transparent opacity-60 hover:opacity-100'
                    }
                  `}
                >
                  <img
                    src={img.url}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Zoom indicator */}
          {zoom !== 1 && (
            <div className="absolute bottom-20 right-4 z-10
              px-3 py-1 rounded bg-black/50 text-white/80 text-sm">
              {Math.round(zoom * 100)}%
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Toolbar Button Component
 */
const ToolbarButton = ({ children, onClick, title, className = '' }) => {
  return (
    <motion.button
      onClick={onClick}
      title={title}
      className={`p-2 rounded-lg text-white/80 hover:text-white
        hover:bg-white/10 transition-colors ${className}`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {children}
    </motion.button>
  );
};

/**
 * Simple Lightbox (single image)
 */
export const SimpleLightbox = ({ 
  image, 
  isOpen, 
  onClose,
}) => {
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && image && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90"
            onClick={onClose}
          />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-lg
              text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image */}
          <motion.img
            src={typeof image === 'string' ? image : image.url}
            alt={typeof image === 'string' ? 'Image' : image.alt}
            className="relative max-w-full max-h-[90vh] object-contain rounded-lg"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
